<template>
  <v-dialog :value="dialog" max-width="520px" @input="onDialogInput">
    <v-card dark class="folder-dialog-card">
      <header class="dialog-header">
        <div class="dialog-icon">
          <v-icon color="#009640">{{ mode === "create" ? "mdi-folder-plus-outline" : "mdi-folder-edit-outline" }}</v-icon>
        </div>
        <div class="dialog-heading">
          <div class="dialog-eyebrow">Ordnerstruktur</div>
          <h2>{{ mode === "create" ? "Neuen Ordner erstellen" : "Ordner bearbeiten" }}</h2>
          <p>
            {{ mode === "create" ? "Vergib einen klaren Namen für den neuen Ablageort." : "Passe Name oder übergeordneten Ordner an." }}
          </p>
        </div>
        <v-btn icon class="dialog-close" aria-label="Ordnerdialog schließen" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <v-form class="dialog-body" @submit.prevent="onSave">
        <label class="field-label" for="folder-name">Ordnername</label>
        <v-text-field
          id="folder-name"
          v-model="localFolder.name"
          placeholder="z. B. Rechnungen 2026"
          prepend-inner-icon="mdi-folder-outline"
          outlined
          hide-details
          autofocus
          class="app-field"></v-text-field>

        <template v-if="mode !== 'create'">
          <label class="field-label parent-label" for="parent-folder">Übergeordneter Ordner</label>
          <v-select
            id="parent-folder"
            v-model="localFolder.parent"
            :items="parentOptions"
            placeholder="Kein übergeordneter Ordner"
            item-text="name"
            item-value="_id"
            prepend-inner-icon="mdi-file-tree-outline"
            clearable
            outlined
            hide-details
            class="app-field"></v-select>
          <div class="field-help">
            <v-icon x-small>mdi-information-outline</v-icon>
            <span>Ohne Auswahl wird der Ordner auf der obersten Ebene angezeigt.</span>
          </div>
        </template>
      </v-form>

      <footer class="dialog-footer">
        <v-btn text class="secondary-button" @click="$emit('close')">Abbrechen</v-btn>
        <v-btn color="#009640" depressed class="primary-button" @click="onSave">
          <v-icon left small>{{ mode === "create" ? "mdi-plus" : "mdi-content-save-outline" }}</v-icon>
          {{ mode === "create" ? "Ordner erstellen" : "Speichern" }}
        </v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "FolderDialog",
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    folderData: {
      type: Object,
      required: true,
    },
    parentOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localFolder: { ...this.folderData },
    }
  },
  watch: {
    folderData: {
      immediate: true,
      handler(newVal) {
        this.localFolder = { ...newVal }
      },
    },
  },
  methods: {
    onDialogInput(value) {
      if (!value) this.$emit("close")
    },
    onSave() {
      this.$emit("save-folder", this.localFolder)
    },
  },
}
</script>

<style scoped>
.folder-dialog-card {
  --dialog-bg: rgba(28, 28, 29, 0.97);
  --dialog-surface: rgba(255, 255, 255, 0.045);
  --dialog-border: rgba(255, 255, 255, 0.09);
  --dialog-border-strong: rgba(255, 255, 255, 0.15);
  --dialog-text: #f5f5f5;
  --dialog-muted: #a3a3a3;
  overflow: hidden;
  border: 1px solid var(--dialog-border-strong);
  border-radius: 22px !important;
  background: var(--dialog-bg) !important;
  backdrop-filter: blur(22px);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.46);
}

.dialog-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 22px 19px;
  border-bottom: 1px solid var(--dialog-border);
}

.dialog-icon {
  display: grid;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 15px;
  background: rgba(0, 150, 64, 0.1);
}

.dialog-heading {
  min-width: 0;
  flex: 1;
}

.dialog-eyebrow {
  margin-bottom: 3px;
  color: #65c98f;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dialog-heading h2 {
  margin: 0;
  color: var(--dialog-text);
  font-size: 1.12rem;
  font-weight: 800;
  letter-spacing: -0.015em;
}

.dialog-heading p {
  margin: 5px 0 0;
  color: var(--dialog-muted);
  font-size: 0.78rem;
  line-height: 1.5;
}

.dialog-close {
  min-width: 44px !important;
  min-height: 44px !important;
  margin-top: -5px;
  margin-right: -7px;
}

.dialog-body {
  padding: 22px;
}

.field-label {
  display: block;
  margin: 0 0 8px 2px;
  color: #d4d4d4;
  font-size: 0.75rem;
  font-weight: 750;
}

.parent-label {
  margin-top: 18px;
}

.app-field ::v-deep .v-input__slot {
  min-height: 48px !important;
  border-radius: 14px !important;
  background: rgba(255, 255, 255, 0.04) !important;
}

.app-field ::v-deep fieldset {
  border-color: var(--dialog-border-strong) !important;
}

.app-field.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.app-field.v-input--is-focused ::v-deep .v-label,
.app-field.v-input--is-focused ::v-deep .v-icon {
  color: #65c98f !important;
}

.folder-dialog-card ::v-deep .primary--text {
  color: #009640 !important;
  caret-color: #009640 !important;
}

.field-help {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 3px 0;
  color: var(--dialog-muted);
  font-size: 0.7rem;
  line-height: 1.45;
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-height: 74px;
  padding: 13px 22px calc(13px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--dialog-border);
  background: rgba(22, 22, 23, 0.96);
}

.primary-button,
.secondary-button {
  min-height: 44px !important;
  border-radius: 13px !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-button:not(.v-btn--disabled) {
  background-color: #009640 !important;
  color: #fff !important;
}

@media (max-width: 599px) {
  .folder-dialog-card {
    margin: 8px;
    border-radius: 20px !important;
  }

  .dialog-header {
    padding: 18px 16px 16px;
  }

  .dialog-icon {
    flex-basis: 42px;
    width: 42px;
    height: 42px;
    border-radius: 13px;
  }

  .dialog-heading p {
    display: none;
  }

  .dialog-body {
    padding: 18px 16px;
  }

  .dialog-footer {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }

  .dialog-footer .v-btn {
    min-width: 0 !important;
    padding: 0 13px !important;
  }
}
</style>
